// src/pages/AppOrdenCobroReactTable.jsx
import React, { useState, useEffect, useMemo, useCallback } from "react";
import styled from "styled-components";
import { useFilters, useSortBy, usePagination } from "react-table";
import moment from "moment";
import Table from "../components/TableContainer";
import ModalVerOrdenCobro from "../components/ModalVerOrdenCobro";

import {
  listOrdenesCobro,
  anularOrdenCobro,
  getOrdenCobroByNro,
} from "../helpers/rutaOrdenesCobro";
import {
  generarPDFOrdenCobro,
  generarPDFOrdenCobroPorNro,
} from "../helpers/pdfOrdenCobro";

const EMAIL_PUEDE_ANULAR_OC = "pablonsaya@hotmail.com";

const parseJSONSafe = (value) => {
  try {
    return value ? JSON.parse(value) : null;
  } catch (_) {
    return null;
  }
};

const getUsuarioActual = () => {
  // Primero intenta desde localStorage si tu app guarda el usuario ahí
  const posiblesKeys = ["usuario", "user", "usuarioLogueado", "currentUser"];

  for (const key of posiblesKeys) {
    const obj = parseJSONSafe(localStorage.getItem(key));
    if (obj?.email || obj?.correo || obj?.usuario?.email || obj?.user?.email) {
      return obj;
    }
  }

  // Después intenta leerlo desde el token JWT
  try {
    const token = localStorage.getItem("token") || "";
    const payload = token.split(".")[1];
    if (!payload) return null;

    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      "="
    );

    const decoded = JSON.parse(window.atob(padded));
    return decoded?.usuario || decoded?.user || decoded;
  } catch (_) {
    return null;
  }
};

const getEmailUsuario = (u) =>
  String(
    u?.email ||
      u?.correo ||
      u?.usuario?.email ||
      u?.user?.email ||
      ""
  )
    .toLowerCase()
    .trim();

const Styles = styled.div`
  .table-container { overflow: auto; height: 600px; }
  /* Forzamos que respete anchos declarados y permita scroll horizontal */
  .table-container table { 
    border: 1px solid black; 
    border-spacing: 0; 
    font-size: 13px; 
    table-layout: fixed;          /* clave: respeta widths */
    width: max-content;           /* crece según suma de columnas */
  }
  th, td { 
    padding: 0.5rem; 
    border-bottom: 1px solid black; 
    border-right: 1px solid black; 
    white-space: nowrap;          /* no cortar texto */
  }
  th { 
    background-color: #778899; 
    position: sticky; top: 0; z-index: 2; 
    text-align: center; font-size: 12px; 
  }
  td { background-color: white; }
  .pagination { padding: 0.5rem; background-color: #778899; font-size: 15px; font-weight: bold; }

  /* ===== Ajustes de ancho por índice de columna =====
     Orden actual:
     1 Ver | 2 PDF | 3 Anular | 4 N° Orden | 5 Fecha | 6 Cliente | 7 ΣAplic | 8 ΣMedios | 9 ΣRet | 10 Estado | 11 Creada por
     Si cambias el orden de columnas, ajustá los nth-child de abajo.
  */
  .table-container table th:nth-child(1),
  .table-container table td:nth-child(1),
  .table-container table th:nth-child(2),
  .table-container table td:nth-child(2),
  .table-container table th:nth-child(3),
  .table-container table td:nth-child(3) {
    width: 65px;
    min-width: 65px;
    text-align: center;
  }

  .table-container table th:nth-child(4),
  .table-container table td:nth-child(4) {
    width: 200px;
    min-width: 200px;
  }

  .table-container table th:nth-child(5),
  .table-container table td:nth-child(5) {
    width: 110px;
    min-width: 110px;
  }

  .table-container table th:nth-child(6),
  .table-container table td:nth-child(6) {
    width: 250px;
    min-width: 250px;
  }

  .table-container table th:nth-child(7),
  .table-container table td:nth-child(7),
  .table-container table th:nth-child(8),
  .table-container table td:nth-child(8),
  .table-container table th:nth-child(9),
  .table-container table td:nth-child(9) {
    width: 140px;
    min-width: 140px;
    text-align: right;
  }

  .table-container table th:nth-child(10),
  .table-container table td:nth-child(10) {
    width: 100px;
    min-width: 100px;
  }

  .table-container table th:nth-child(11),
  .table-container table td:nth-child(11) {
    width: 160px;
    min-width: 160px;
  }

`;

const OrdenCobroHeader = styled.div`
  display: flex;
  align-items: center;

  min-height: 78px;

  margin-bottom: 12px;

  padding: 14px 18px;

  border: 1px solid #dce5f0;
  border-radius: 14px;

  background:
    linear-gradient(
      120deg,
      #ffffff 0%,
      #f8fafc 65%,
      #eef4fb 100%
    );

  box-shadow:
    0 3px 14px
    rgba(15, 23, 42, 0.035);

  .header-icon {
    width: 46px;
    height: 46px;

    flex: 0 0 46px;

    display: flex;
    align-items: center;
    justify-content: center;

    margin-right: 13px;

    border-radius: 11px;

    background: #eaf2ff;

    color: #2868c6;

    font-size: 19px;
  }

  .header-text span {
    display: block;

    margin-bottom: 2px;

    color: #5f7fa8;

    font-size: 9px;
    font-weight: 700;

    letter-spacing: 1.7px;
  }

  .header-text h3 {
    margin: 0 0 3px 0;

    color: #18243a;

    font-size: 20px;
    font-weight: 700;
  }

  .header-text p {
    margin: 0;

    color: #748196;

    font-size: 10px;
  }
`;

const OrdenCobroFiltros = styled.div`
  display: flex;
  align-items: end;

  gap: 8px;

  flex-wrap: wrap;

  margin-bottom: 12px;

  padding: 14px;

  border: 1px solid #e2e8f0;
  border-radius: 12px;

  background: #ffffff;

  box-shadow:
    0 3px 14px
    rgba(15, 23, 42, 0.035);

  .oc-field {
    display: flex;
    flex-direction: column;

    gap: 5px;
  }

  .oc-field label {
    margin: 0;

    color: #59687d;

    font-size: 10px;
    font-weight: 600;
  }

  .oc-field input,
  .oc-field select {
    height: 34px;

    padding: 0 8px;

    border: 1px solid #d7e0ea;
    border-radius: 7px;

    background: #ffffff;

    color: #24324a;

    font-size: 10.5px;
  }

  .oc-date {
    width: 150px;
  }

  .oc-state {
    width: 145px;
  }

  .oc-actions {
    display: flex;

    gap: 6px;

    flex-wrap: wrap;
  }

  .oc-actions button {
    min-height: 34px;

    padding: 5px 11px;

    border-radius: 7px;

    font-size: 10px;
    font-weight: 600;

    white-space: nowrap;
  }

  @media (max-width: 768px) {
    .oc-field,
    .oc-date,
    .oc-state {
      width: 100%;
    }

    .oc-actions {
      width: 100%;
    }
  }
`;

export default function AppOrdenCobroReactTable() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  // filtros
  const [desde, setDesde] = useState(moment().startOf("month").format("YYYY-MM-DD"));
  const [hasta, setHasta] = useState(moment().format("YYYY-MM-DD"));
  const [estado, setEstado] = useState(""); // "", "ACTIVA", "ANULADA"

  // visor OC
  const [showVerOC, setShowVerOC] = useState(false);
  const [verOcId, setVerOcId] = useState(null);

  const puedeAnularOC = useMemo(() => {
    const usuario = getUsuarioActual();
    const email = getEmailUsuario(usuario);
    return email === EMAIL_PUEDE_ANULAR_OC;
  }, []);

  const money = (n) =>
    (Number(n || 0)).toLocaleString("es-AR", { style: "currency", currency: "ARS" });

  const fetchData = useCallback(async () => {
    setLoading(true);
    const resp = await listOrdenesCobro({ desde, hasta, estado: estado || undefined });
    if (!resp?.ok) {
      console.error(resp?.msg || resp?.error || "Error al listar OC");
      setData([]);
    } else {
      setData(resp.ordenes || []);
    }
    setLoading(false);
  }, [desde, hasta, estado]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const onAnular = useCallback(async (id) => {
    try {
      if (
        !window.confirm(
          "¿Anular esta Orden de Cobro? Esto revierte sus aplicaciones, movimientos y cartera si corresponde."
        )
      ) {
        return;
      }

      const r = await anularOrdenCobro(id);

      if (r?.ok) {
        alert("Orden anulada.");
        fetchData();
      } else {
        alert(r?.msg || r?.error || r?.err || "No se pudo anular la orden.");
      }
    } catch (error) {
      console.error("Error anulando OC:", error);
      alert(error?.message || "Error inesperado anulando la orden.");
    }
  }, [fetchData]);

  const columns = useMemo(() => [
    {
      id: "ver",
      Header: "Ver",
      accessor: "_id",
      width: 50,
      Cell: ({ row }) => (
        <button
          type="button"
          className="action-btn action-btn--edit"
          title="Ver / Editar"
          onClick={() => { setVerOcId(row.original._id); setShowVerOC(true); }}
        >
          <i className="fa fa-eye" aria-hidden="true" />
        </button>
      ),
    },
    {
      id: "pdf",
      Header: "PDF",
      accessor: "_id",
      width: 50,
      Cell: ({ row }) => (
        <button
          type="button"
          className="action-btn action-btn--pdf"
          title="Generar PDF"
          onClick={() => generarPDFOrdenCobro(row.original._id)}
        >
          <i className="fa fa-file-pdf-o" aria-hidden="true" />
        </button>
      ),
    },

    {
      id: "anular",
      Header: puedeAnularOC ? "Anular" : "",
      accessor: "_id",
      width: 65,
      Cell: ({ row }) => {
        if (!puedeAnularOC) return null;

        return (
          <button
            type="button"
            className="action-btn action-btn--del"
            title="Anular OC"
            disabled={row.original.estado === "ANULADA"}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onAnular(row.original._id);
            }}
          >
            <i className="fa fa-ban" aria-hidden="true" />
          </button>
        );
      },
    },

    {
      Header: "N° Orden Cliente",
      accessor: "nroOrdenCliente",
      width: 200,
      Cell: ({ value }) => (
        <div style={{ textAlign: "left" }}>
          {value || "-"}
        </div>
      ),
    },

    {
      Header: "Fecha",
      accessor: (r) => (r.fecha ? new Date(r.fecha) : ""),
      id: "fecha",
      width: 110,
      Cell: ({ value }) =>
        value instanceof Date ? value.toLocaleDateString("es-AR") : "-",
    },

    { Header: "Cliente", accessor: "cliente.razonsocial", width: 250 },

    {
      Header: () => <div style={{ textAlign: "center" }}>Σ Aplicaciones</div>,
      accessor: "totalAplicaciones",
      id: "totalAplicaciones",
      width: 100,
      Cell: ({ value }) => (
        <div style={{ textAlign: "right" }}>{money(value)}</div>
      ),
      Footer: ({ rows }) => {
        const t = rows.reduce((s, r) => s + (r.values.totalAplicaciones || 0), 0);
        return (
          <div style={{ textAlign: "right", fontWeight: "bold", color: "red" }}>
            {money(t)}
          </div>
        );
      },
    },
    {
      Header: () => <div style={{ textAlign: "center" }}>Σ Medios</div>,
      accessor: "totalMedios",
      id: "totalMedios",
      width: 140,
      Cell: ({ value }) => (
        <div style={{ textAlign: "right" }}>{money(value)}</div>
      ),
      Footer: ({ rows }) => {
        const t = rows.reduce((s, r) => s + (r.values.totalMedios || 0), 0);
        return (
          <div style={{ textAlign: "right", fontWeight: "bold", color: "blue" }}>
            {money(t)}
          </div>
        );
      },
    },
    {
      Header: () => <div style={{ textAlign: "center" }}>Σ Retenciones</div>,
      accessor: "totalRetenciones",
      id: "totalRetenciones",
      width: 140,
      Cell: ({ value }) => (
        <div style={{ textAlign: "right" }}>{money(value)}</div>
      ),
      Footer: ({ rows }) => {
        const t = rows.reduce((s, r) => s + (r.values.totalRetenciones || 0), 0);
        return (
          <div style={{ textAlign: "right", fontWeight: "bold", color: "green" }}>
            {money(t)}
          </div>
        );
      },
    },


    { Header: "Estado", accessor: "estado", width: 100 },
    { Header: "Creada por", accessor: "createdBy.nombres", width: 160 },
  ], [onAnular, puedeAnularOC]);

  return (
    <div className="container mt-4">

      <OrdenCobroHeader>
        <div className="header-icon">
          <i
            className="fa fa-money"
            aria-hidden="true"
          />
        </div>

        <div className="header-text">
          <span>
            INFORMES
          </span>

          <h3>
            Órdenes de Cobro
          </h3>

          <p>
            Consulta, control, edición e
            impresión de órdenes de cobro.
          </p>
        </div>
      </OrdenCobroHeader>

      <OrdenCobroFiltros>

        <div className="oc-field oc-date">
          <label>Fecha desde</label>

          <input
            type="date"
            value={desde}
            onChange={(e) =>
              setDesde(e.target.value)
            }
          />
        </div>

        <div className="oc-field oc-date">
          <label>Fecha hasta</label>

          <input
            type="date"
            value={hasta}
            onChange={(e) =>
              setHasta(e.target.value)
            }
          />
        </div>

        <div className="oc-field oc-state">
          <label>Estado</label>

          <select
            value={estado}
            onChange={(e) =>
              setEstado(e.target.value)
            }
          >
            <option value="">
              Todos
            </option>

            <option value="ACTIVA">
              Activa
            </option>

            <option value="ANULADA">
              Anulada
            </option>
          </select>
        </div>

        <div className="oc-actions">

          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={fetchData}
          >
            <i className="fa fa-search mr-1" />
            Buscar
          </button>

          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={async () => {
              const nro = prompt(
                "N° de orden del cliente (ej: 000123):"
              );

              if (!nro) return;

              const r =
                await getOrdenCobroByNro(nro);

              if (r?.ok) {
                setVerOcId(
                  r.ordenCobro._id
                );

                setShowVerOC(true);
              } else {
                alert(
                  r?.msg ||
                    "No se encontró la Orden de Cobro."
                );
              }
            }}
          >
            <i className="fa fa-eye mr-1" />
            Ver/Editar por N°
          </button>

          <button
            type="button"
            className="btn btn-outline-success btn-sm"
            onClick={async () => {
              const nro = prompt(
                "N° de orden del cliente a imprimir:"
              );

              if (!nro) return;

              await generarPDFOrdenCobroPorNro(
                nro
              );
            }}
          >
            <i className="fa fa-print mr-1" />
            Imprimir por N°
          </button>

        </div>

      </OrdenCobroFiltros>

      <Styles>
        <div className="table-container">
          <Table
            columns={columns}
            data={data}
            loading={loading}
            plugins={[useFilters, useSortBy, usePagination]}
          />
        </div>
      </Styles>

      {showVerOC && verOcId && (
        <ModalVerOrdenCobro
          show={showVerOC}
          ocId={verOcId}
          onHide={(refrescar) => {
            setShowVerOC(false);
            setVerOcId(null);
            if (refrescar) fetchData();
          }}
        />
      )}
    </div>
  );
}
